// ═══════════════════════════════════════════════════════════════
// MÓDULO DE GESTIÓN DE VACACIONES
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { useVacationPolicies, useVacationBalances, useVacationHistory } from '../../hooks/useVacations';
import PoliciesTab  from './tabs/PoliciesTab';
import BalanceTab   from './tabs/BalanceTab';
import HistorialTab from './tabs/HistorialTab';
import CalendarTab  from './tabs/CalendarTab';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { employeesAPI } from '../../api/employees';

export default function VacationsModule({ currentUser }) {
  const { can } = usePermissionsContext();
  const [activeTab, setActiveTab]     = useState('balance');
  const [year, setYear]               = useState(new Date().getFullYear());
  const [historyFilters, setHistoryFilters] = useState({ employeeId: null, year: null });
  const [toast, setToast]             = useState({ show: false, message: '', type: 'success' });
  const [selfEmployeeId, setSelfEmployeeId] = useState(null);

  const isAdmin    = can('VACATION_APPROVE');
  const isEmployee = currentUser?.role === 'employee';

  // Cargar el ID del empleado vinculado al usuario actual (solo para rol employee)
  useEffect(() => {
    if (!isEmployee) return;
    employeesAPI.getAll().then(res => {
      const emp = res.employees?.[0];
      if (emp) {
        setSelfEmployeeId(emp.id);
        setHistoryFilters({ employeeId: emp.id, year: null });
      }
    }).catch(() => {});
  }, [isEmployee]);

  const TABS = isEmployee
    ? [
        { id: 'balance',   label: 'Balance'   },
        { id: 'historial', label: 'Historial' },
      ]
    : [
        { id: 'balance',    label: 'Balance'    },
        { id: 'historial',  label: 'Historial'  },
        { id: 'calendario', label: 'Calendario' },
        ...(isAdmin ? [{ id: 'policies', label: 'Políticas' }] : []),
      ];

  // ── Hooks ───────────────────────────────────────────────────
  const {
    policies, loading: loadingPolicies, error: errorPolicies,
    save: savePolicy, remove: deletePolicy, initDefaults,
  } = useVacationPolicies();

  const {
    balances: allBalances, loading: loadingBalances, error: errorBalances,
    allocate, adjust,
  } = useVacationBalances(year);

  // Empleados solo ven su propio balance
  const balances = isEmployee && selfEmployeeId
    ? allBalances.filter(b => b.employee_id === selfEmployeeId)
    : allBalances;

  const {
    transactions, loading: loadingHistory, error: errorHistory,
    load: reloadHistory,
  } = useVacationHistory(historyFilters.employeeId, historyFilters.year);

  // ── Auto-dismiss toast ──────────────────────────────────────
  useEffect(() => {
    if (!toast.show) return;
    const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    return () => clearTimeout(timer);
  }, [toast.show]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // ── Handlers ────────────────────────────────────────────────
  const handleSavePolicy = async (data) => {
    try {
      await savePolicy(data);
      showToast(data.id ? 'Política actualizada correctamente.' : 'Política creada correctamente.');
    } catch (err) {
      showToast(err?.response?.data?.error || err?.message || 'Error al guardar la política.', 'error');
      throw err; // re-lanzar para que el tab maneje el error inline
    }
  };

  const handleDeletePolicy = async (id) => {
    try {
      await deletePolicy(id);
      showToast('Política eliminada.');
    } catch (err) {
      showToast(err?.response?.data?.error || err?.message || 'Error al eliminar.', 'error');
      throw err;
    }
  };

  const handleInitDefaults = async () => {
    try {
      await initDefaults();
      showToast('Políticas por defecto creadas (Ley 16-92).');
    } catch (err) {
      showToast(err?.response?.data?.error || err?.message || 'Error al crear políticas por defecto.', 'error');
      throw err;
    }
  };

  const handleAllocate = async (employeeId, recalculate = false) => {
    try {
      await allocate(employeeId, recalculate);
      showToast(
        recalculate
          ? 'Asignación recalculada y sobrescrita.'
          : employeeId ? 'Días asignados al empleado.' : 'Días asignados a todos los empleados.'
      );
    } catch (err) {
      showToast(err?.response?.data?.error || err?.message || 'Error al asignar días.', 'error');
      throw err;
    }
  };

  const handleAdjust = async (employeeId, days, notes, field) => {
    try {
      await adjust(employeeId, days, notes, field);
      showToast('Balance ajustado correctamente.');
    } catch (err) {
      showToast(err?.response?.data?.error || err?.message || 'Error al ajustar balance.', 'error');
      throw err;
    }
  };

  const handleHistoryFilter = ({ employeeId, year: filterYear }) => {
    setHistoryFilters({ employeeId, year: filterYear });
  };

  // Recargar historial cuando cambian los filtros
  useEffect(() => {
    if (activeTab === 'historial') {
      reloadHistory();
    }
    // eslint-disable-next-line
  }, [historyFilters, activeTab]);

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error global */}
      {(errorPolicies || errorBalances || errorHistory) && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Error al cargar datos</p>
            <p className="text-xs mt-0.5 opacity-80">
              {errorPolicies || errorBalances || errorHistory}
            </p>
          </div>
        </div>
      )}

      {/* Contenido del tab activo */}
      {activeTab === 'balance' && (
        <BalanceTab
          balances={balances}
          loading={loadingBalances}
          year={year}
          onYearChange={setYear}
          onAllocate={isEmployee ? null : handleAllocate}
          onAdjust={isEmployee ? null : handleAdjust}
          readOnly={isEmployee}
        />
      )}

      {activeTab === 'historial' && (
        <HistorialTab
          transactions={transactions}
          loading={loadingHistory}
          onFilter={isEmployee ? null : handleHistoryFilter}
          readOnly={isEmployee}
        />
      )}

      {activeTab === 'calendario' && (
        <CalendarTab />
      )}

      {activeTab === 'policies' && isAdmin && (
        <PoliciesTab
          policies={policies}
          loading={loadingPolicies}
          onSave={handleSavePolicy}
          onDelete={handleDeletePolicy}
          onInitDefaults={handleInitDefaults}
        />
      )}

      {/* Toast de notificación */}
      {toast.show && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
            toast.type === 'success'
              ? 'bg-teal-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
